import { useEffect, useState } from 'react';
import type { EditorSDK } from '../sdk';
import type { EditorSdkEvents } from '../types';
import { useEditorEvent } from './useEditorEvent';

type EditorStates =
  | 'loaded'
  | 'draft'
  | 'modifications'
  | 'edits'
  | 'redos'
  | 'inspectAction'
  | 'variables'
  | 'findAndReplaceRules'
  | 'codeSnippets'
  | 'chartsTemplates'
  | 'recordingType'
  | 'metadata'
  | 'transactionModifications'
  | 'navigationModifications'
  | 'spotEditsModifications'
  | 'tableCraftObjects'
  | 'tableCraftRows'
  | 'tableCraftFunctions'
  | 'guidanceTooltipsModifications'
  | 'blockInteractionsSettings'
  | 'isGuidanceTooltipsEditLoading'
  | 'isGuidanceTooltipOpen'
  | 'aiPromptGroup'
  | 'performanceResults'
  | 'isPerformanceMeasuringStarted'
  | 'performanceSelectorXpath';

const stateNameToEvent: Record<EditorStates, keyof EditorSdkEvents> = {
  loaded: 'loaded',
  draft: 'updatedDraft',
  modifications: 'updatedModifications',
  edits: 'updatedEdits',
  redos: 'updatedRedos',
  inspectAction: 'updatedInspectAction',
  variables: 'updatedVariables',
  findAndReplaceRules: 'updatedFindAndReplaceRules',
  codeSnippets: 'updatedCodeSnippets',
  chartsTemplates: 'updatedChartTemplates',
  recordingType: 'updatedRecordingType',
  metadata: 'updateMetadata',
  transactionModifications: 'updatedTransactionModifications',
  navigationModifications: 'updatedNavigationModifications',
  spotEditsModifications: 'updatedSpotEditsModifications',
  tableCraftObjects: 'updatedTableCraftObjects',
  tableCraftRows: 'updatedTableCraftRows',
  tableCraftFunctions: 'updatedTableCraftFunctions',
  guidanceTooltipsModifications: 'updatedGuidanceTooltipsModifications',
  blockInteractionsSettings: 'updatedBlockInteractionsSettings',
  isGuidanceTooltipsEditLoading: 'isGuidanceTooltipsEditLoading',
  isGuidanceTooltipOpen: 'isGuidanceTooltipOpen',
  aiPromptGroup: 'updatedAiPromptGroup',
  performanceResults: 'performanceResults',
  isPerformanceMeasuringStarted: 'isPerformanceMeasuringStarted',
  performanceSelectorXpath: 'updatedPerformanceSelectorXpath',
};

export const useEditorState = <T extends keyof Pick<EditorSDK, EditorStates>>(
  editorSdk: EditorSDK,
  stateName: T
) => {
  const [state, setState] = useState(editorSdk[stateName]);

  useEditorEvent(editorSdk, stateNameToEvent[stateName], setState as any);

  useEffect(() => {
    if (state !== editorSdk[stateName]) {
      setState(editorSdk[stateName]);
    }
  }, [state, editorSdk, stateName]);

  return state;
};
